import {
  Badge,
  Box,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  HStack,
  Heading,
  Image,
  Skeleton,
  SkeletonText,
  Text,
  VStack,
} from "@chakra-ui/react";
import "./AccommodationCard.scss";
import { useTranslation } from "react-i18next";
import Amenity from "../accommodation/Amenity";

const CARD_WIDTH = { base: "100%", lg: "600px" };
const IMAGE_WIDTH = { base: "160px", sm: "220px" };
// Below lg, full-width cards often wrap the address to two lines; a fixed max height clips the footer (price).
const CARD_MAX_H = { base: "none", lg: "240px" };

/** Booking-style price stack: dominant nightly rate, subdued per-night label, secondary total (same data as before). */
const AccommodationCardPrices = ({ pricePerNight, totalPrice, t }) => (
  <Box
    dir="ltr"
    flexShrink={0}
    textAlign="right"
    minW="min-content"
    maxW="100%"
  >
    {/* Base: stack so "per night" is never clipped beside a wide price; sm+: one row */}
    <Flex
      align={{ base: "flex-end", sm: "baseline" }}
      justify="flex-end"
      flexDir={{ base: "column", sm: "row" }}
      gap={{ base: 0, sm: 1 }}
      rowGap={{ base: 0.5 }}
    >
      <Text
        as="span"
        fontWeight="700"
        fontSize={{ base: "xl", sm: "2xl" }}
        lineHeight="1.1"
        letterSpacing="tight"
        color="gray.800"
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        {pricePerNight}
      </Text>
      <Text
        as="span"
        fontSize="xs"
        color="gray.600"
        fontWeight="normal"
        whiteSpace="nowrap"
        lineHeight="1.2"
      >
        {t("perNightLabel")}
      </Text>
    </Flex>
    <Text fontSize="xs" color="gray.600" mt={1} lineHeight="short">
      {t("bookingTotal")}{" "}
      <Text
        as="span"
        fontWeight="semibold"
        color="gray.700"
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        {totalPrice}
      </Text>
    </Text>
  </Box>
);

const AccommodationCardSkeleton = () => {
  return (
    <Card
      w={CARD_WIDTH}
      variant="unstyled"
      className="accommodation-card"
      borderRadius="none"
      display="flex"
      flexDir="row"
      alignItems="stretch"
      minH="200px"
      maxH={CARD_MAX_H}
    >
      <Skeleton minW={IMAGE_WIDTH} w={IMAGE_WIDTH} />
      <Box w="100%" padding="16px" display="flex" flexDir="column">
        <SkeletonText skeletonHeight="5" width="200px" noOfLines={1} mb={3} />
        <SkeletonText noOfLines={2} spacing={3} mb={3} />
        <Skeleton height="20px" width="120px" borderRadius="sm" mb="auto" />
        <Flex justify="space-between" align="flex-end" gap={3} mt={2}>
          <Skeleton height="22px" width="56px" borderRadius="md" />
          <Skeleton height="48px" width="100px" borderRadius="md" />
        </Flex>
      </Box>
    </Card>
  );
};

const AccommodationCard = ({ data }) => {
  const { t } = useTranslation("common");
  const {
    name,
    address,
    amenities,
    rating,
    previewImageUrl,
    pricePerNight,
    totalPrice,
  } = data;

  // iterate through amenities and return the first two that are true
  const [firstAmenity = false, secondAmenity = false] = Object.keys(
    amenities
  ).reduce((acc, key) => {
    if (amenities[key]) {
      acc.push(key);
    }
    return acc;
  }, []);

  const cardPadding = "12px";

  return (
    <Card
      className="accommodation-card"
      variant="outline"
      borderRadius="lg"
      w={CARD_WIDTH}
      display="flex"
      flexDir="row"
      alignItems="stretch"
      minH="200px"
      maxH={CARD_MAX_H}
    >
      <Box flexShrink={0} w={IMAGE_WIDTH} minW={IMAGE_WIDTH} h="100%">
        <Image
          src={previewImageUrl}
          alt={name}
          h="100%"
          objectFit="cover"
          w="100%"
        />
      </Box>
      <VStack w="100%" spacing={0} align="stretch" flex={1} minW={0}>
        <CardHeader padding={cardPadding}>
          <Box>
            <Heading fontSize="xl" paddingBottom={1}>
              {name}
            </Heading>
            <Text fontSize="sm" color="gray.600" lineHeight="short">
              {address}
            </Text>
          </Box>
        </CardHeader>
        <CardBody padding={cardPadding} flex="1">
          <HStack spacing={2} wrap="wrap">
            <Amenity
              amenityKey={firstAmenity}
              size={{ base: "xs", sm: "sm" }}
            />
            <Amenity
              amenityKey={secondAmenity}
              size={{ base: "xs", sm: "sm" }}
            />
          </HStack>
        </CardBody>
        <CardFooter
          padding={cardPadding}
          justify="space-between"
          align={{ base: "flex-end", lg: "center" }}
          flexWrap="wrap"
          gap={2}
          mt="auto"
        >
          <Badge
            fontSize="small"
            colorScheme="whatsapp"
            borderRadius="sm"
            px={2.5}
            py={1}
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            lineHeight="1.25"
            minH="1.75rem"
            flexShrink={0}
            alignSelf={{ base: "flex-end", lg: "center" }}
          >
            {rating}
          </Badge>
          <AccommodationCardPrices
            pricePerNight={pricePerNight}
            totalPrice={totalPrice}
            t={t}
          />
        </CardFooter>
      </VStack>
    </Card>
  );
};

export default AccommodationCard;
export { AccommodationCardSkeleton };
